'use client';

export interface LegislationVersion {
  ucinnyDo: string;
  ucinnyOd: string;
  iri: string;
  real: boolean;
  caption?: string;
  citacia?: string;
  cesta?: string;
  oznacenie?: string | null;
  suvisiSAktualnymRezom?: boolean;
}

export interface TextDiff {
  aktualne: {
    text: string;
    ucinnyOd: string;
  };
  porovnavane: {
    text: string;
    ucinnyOd: string;
  };
}

// Cache for storing API responses
class ApiCache {
  private cache = new Map<string, { data: unknown; timestamp: number }>();
  private readonly TTL = 5 * 60 * 1000; // 5 minutes cache TTL

  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    if (!item) return null;

    // Check if item has expired
    if (Date.now() - item.timestamp > this.TTL) {
      this.cache.delete(key);
      return null;
    }

    return item.data as T;
  }

  set<T>(key: string, data: T): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  clear(): void {
    this.cache.clear();
  }

  // Optional: Clean up expired items
  cleanup(): void {
    const now = Date.now();
    for (const [key, item] of this.cache.entries()) {
      if (now - item.timestamp > this.TTL) {
        this.cache.delete(key);
      }
    }
  }
}

// Request deduplication - prevent multiple requests for the same data
class RequestDeduplicator {
  private pendingRequests = new Map<string, Promise<unknown>>();

  async deduplicate<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    // If there's already a pending request for this key, return it
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key) as Promise<T>;
    }

    // Create new request
    const request = requestFn().finally(() => {
      // Remove from pending requests when completed
      this.pendingRequests.delete(key);
    });

    // Store the pending request
    this.pendingRequests.set(key, request);

    return request;
  }
}

// Global instances
const apiCache = new ApiCache();
const requestDeduplicator = new RequestDeduplicator();

// Common headers for API requests
const API_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'User-Agent':
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/122.0.0.0 Safari/537.36',
  Origin: 'https://www.slov-lex.sk',
  Referer: 'https://www.slov-lex.sk/',
} as const;

function buildElementId(elementId: string): string {
  return elementId
    ?.split('.')
    .filter((part) => part !== 'text')
    .join('/');
}

/**
 * Client-side function to fetch legislation versions with caching and deduplication
 */
export async function fetchLegislationVersions(
  iri: string,
  elementId?: string | undefined
): Promise<LegislationVersion[]> {
  if (!iri) {
    throw new Error('IRI parameter is required');
  }

  // Create cache key
  const cacheKey = `versions:${iri}:${elementId || ''}`;

  // Check cache first
  const cachedData = apiCache.get<LegislationVersion[]>(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  // Use request deduplication
  return requestDeduplicator.deduplicate(cacheKey, async () => {
    try {
      // Extract the path and IRI znenia parameters from the provided IRI
      const pathMatch = iri.split('/').slice(0, -1).join('/');
      const path = pathMatch;

      // Add element ID by splitting from '.' and combining by '/'
      const elementIdParts = buildElementId(elementId ?? '');
      const fullPath = elementIdParts ? `${path}/${elementIdParts}` : path;

      // Build the API URL
      const apiUrl = `https://api-gateway.slov-lex.sk/vizualizacia/rozsirene_zobrazenie/rezy_ustanovenia/?cesta=${fullPath}&iri_znenia=${iri}`;

      // Fetch data from the external API
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: API_HEADERS,
      });

      if (!response.ok) {
        console.error('Failed to fetch versions:', {
          status: response.status,
          statusText: response.statusText,
          url: apiUrl,
        });
        throw new Error(
          `Failed to fetch versions: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        console.warn('Unexpected response format (not an array):', data);
        const emptyResult: LegislationVersion[] = [];
        apiCache.set(cacheKey, emptyResult);
        return emptyResult;
      }

      // Cache the result
      apiCache.set(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Error fetching legislation versions:', error);
      throw error;
    }
  });
}

/**
 * Client-side function to fetch legislation text comparison with caching and deduplication
 */
export async function fetchLegislationComparison(
  aktualne: string,
  porovnavane: string,
  elementId: string
): Promise<TextDiff[]> {
  if (!aktualne || !porovnavane) {
    throw new Error('Both aktualne and porovnavane parameters are required');
  }

  // Create cache key
  const cacheKey = `comparison:${aktualne}:${porovnavane}:${elementId || ''}`;

  // Check cache first
  const cachedData = apiCache.get<TextDiff[]>(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  // Use request deduplication
  return requestDeduplicator.deduplicate(cacheKey, async () => {
    try {
      const elementIdParts = buildElementId(elementId ?? '');
      const path = aktualne.split('/');
      let newPath = '';

      // if path has the length of more than or equal to 5, then we first add 5 elements to the path and then add the elementIdParts and then add the rest of the path
      if (path.length >= 5) {
        newPath = `${path.slice(0, 5).join('/')}/${elementIdParts}/${path.slice(5).join('/')}`;
      } else {
        newPath = `${path.join('/')}/${elementIdParts}`;
      }

      // Build the API URL for fetching text differences
      const apiUrl = `https://api-gateway.slov-lex.sk/vizualizacia/rozsirene_zobrazenie/diff_text/?aktualne_iri=${newPath}&porovnavane_iri=${porovnavane}`;

      // Fetch data from the external API
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: API_HEADERS,
      });

      if (!response.ok) {
        console.error('Failed to fetch comparison:', {
          status: response.status,
          statusText: response.statusText,
          url: apiUrl,
          params: { aktualne, porovnavane },
        });

        // Cache empty result to prevent repeated failed requests
        const emptyResult: TextDiff[] = [];
        apiCache.set(cacheKey, emptyResult);
        return emptyResult;
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        console.warn('Unexpected response format (not an array):', data);
        const emptyResult: TextDiff[] = [];
        apiCache.set(cacheKey, emptyResult);
        return emptyResult;
      }

      // Cache the result
      apiCache.set(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Error fetching legislation comparison:', error);
      // Cache empty result to prevent repeated failed requests
      const emptyResult: TextDiff[] = [];
      apiCache.set(cacheKey, emptyResult);
      return emptyResult;
    }
  });
}

// Automatically cleanup expired cache entries every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(
    () => {
      apiCache.cleanup();
    },
    5 * 60 * 1000
  );
}
